import { useEffect, useState, useCallback } from 'react'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Reseller Course Payments — the reseller's own "call the course & pay" worklist
 * for their reseller-paid tee times (WhoPaysCourse='reseller', incl. request-mode
 * courses). InteGolf-paid rounds are settled by InteGolf, not shown here.
 *
 * Pay the course, then Mark paid (records who/when + reference + actual amount).
 * Overdue-unpaid pinned on top; card-on-file courses show a badge but still need
 * a Mark done. The card-on-file flag itself is managed on Connected Courses.
 */
const money = (n, cur = 'CAD') => (n == null ? '—' : `${cur} ${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
const fmtDate = v => { if (!v) return '—'; const d = new Date(String(v).slice(0, 10) + 'T00:00:00'); return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
const fmtTime = v => { if (!v) return ''; const m = String(v).match(/(\d{1,2}):(\d{2})/); if (!m) return String(v); let h = Number(m[1]); const ap = h >= 12 ? 'PM' : 'AM'; h = h % 12 || 12; return `${h}:${m[2]} ${ap}` }

export default function CoursePayments() {
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [counts, setCounts] = useState({ unpaid: 0, overdue: 0 })
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('unpaid')
  const [page, setPage] = useState(1)
  const pageSize = 25
  const [busy, setBusy] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    tournamentApi.get('/portal/reseller/course-payments', { params: { search: search || undefined, status, page, pageSize } })
      .then(r => { setData(r.data); setCounts(r.data.counts || { unpaid: 0, overdue: 0 }); setErr(null) })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false))
  }, [search, status, page])
  useEffect(() => { load() }, [load])

  async function markPaid(row, form) {
    setBusy(row.bookingRoundId); setErr(null)
    try {
      await tournamentApi.post(`/portal/reseller/course-payments/${row.bookingRoundId}/pay`, {
        reference: form.reference || null,
        amountPaid: form.amountPaid === '' ? null : Number(form.amountPaid),
        note: form.note || null,
      })
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }
  async function undo(row) {
    if (!confirm(t('sp_cp_undo_confirm', 'Mark this tee time as unpaid again?'))) return
    setBusy(row.bookingRoundId); setErr(null)
    try { await tournamentApi.post(`/portal/reseller/course-payments/${row.bookingRoundId}/undo`, {}); load() }
    catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }

  const rows = data?.rows || []
  const total = data?.total || 0
  const pages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <ResellerShell title={t('sp_cp_title', 'Course payments')}>
      <p style={{ color: '#64748b', fontSize: 14, marginTop: 0, marginBottom: 16, maxWidth: 720, lineHeight: 1.5 }}>
        {t('sp_cp_intro', 'Tee times you owe the course for. Call the course, pay for the round, then mark it done here with a reference number.')}
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1) }}
          placeholder={t('sp_cp_search_ph', 'Search course or player…')}
          style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, minWidth: 260 }}
        />
        <select value={status} onChange={e => { setStatus(e.target.value); setPage(1) }}
          style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}>
          <option value="unpaid">{t('sp_cp_unpaid', 'Unpaid')}</option>
          <option value="overdue">{t('sp_cp_overdue', 'Overdue')}</option>
          <option value="paid">{t('sp_cp_paid', 'Paid')}</option>
          <option value="">{t('sp_cp_all', 'All')}</option>
        </select>
        <span style={{ fontSize: 12.5, color: '#64748b' }}>
          <b style={{ color: '#b45309' }}>{counts.overdue}</b> {t('sp_cp_overdue', 'Overdue').toLowerCase()} · <b>{counts.unpaid}</b> {t('sp_cp_unpaid', 'Unpaid').toLowerCase()}
        </span>
      </div>

      {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{err}</div>}

      {loading && data === null ? <div style={{ color: '#64748b' }}>{t('sp_loading', 'Loading…')}</div>
        : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc', color: '#64748b' }}>
            {t('sp_cp_none', 'No tee times to pay right now.')}
          </div>
        ) : (
          <div style={{ border: '1px solid #eef2f7', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 150px 120px 120px', gap: 8, padding: '10px 14px', background: '#f8fafc', fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: 0.4, textTransform: 'uppercase' }}>
              <div>{t('sp_cp_col_teetime', 'Date / time')}</div>
              <div>{t('sp_cp_col_course', 'Course')}</div>
              <div>{t('sp_cp_col_player', 'Main player')}</div>
              <div>{t('sp_cp_col_amount', 'Amount')}</div>
              <div>{t('sp_cp_col_status', 'Status')}</div>
            </div>
            {rows.map(row => (
              <Row key={row.bookingRoundId} row={row} busy={busy} onPay={markPaid} onUndo={undo} t={t} />
            ))}
          </div>
        )}

      {total > pageSize && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={pgBtn(page <= 1)}>← {t('sp_prev', 'Prev')}</button>
          <span style={{ fontSize: 13, color: '#64748b' }}>{t('sp_conn_page_of', 'Page {page} of {pages}', { page, pages })}</span>
          <button disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))} style={pgBtn(page >= pages)}>{t('sp_next', 'Next')} →</button>
        </div>
      )}
    </ResellerShell>
  )
}

function Row({ row, busy, onPay, onUndo, t }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ reference: '', amountPaid: row.expectedCost ?? '', note: '' })
  const rowBusy = busy === row.bookingRoundId
  const inp = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5, width: '100%', boxSizing: 'border-box' }

  return (
    <div style={{ borderTop: '1px solid #f1f5f9', background: row.isOverdue && !row.isPaid ? '#fffbeb' : '#fff', borderLeft: row.isOverdue && !row.isPaid ? '3px solid #f59e0b' : '3px solid transparent' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr 150px 120px 120px', gap: 8, padding: '11px 14px', alignItems: 'center', fontSize: 13 }}>
        <div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{fmtDate(row.playDate)}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{fmtTime(row.teeTime)}</div>
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.courseName || `Course #${row.coursesId}`}</div>
          {row.cardOnFile && <span style={{ fontSize: 11, color: '#0369a1', background: '#e0f2fe', padding: '1px 7px', borderRadius: 999, marginTop: 2, display: 'inline-block' }}>{t('sp_cp_card_on_file', 'Card on file')}</span>}
        </div>
        <div style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.mainPlayer || '—'} <span style={{ color: '#94a3b8' }}>×{row.golfers}</span>
        </div>
        <div style={{ fontWeight: 600 }}>{money(row.expectedCost, row.currency)}</div>
        <div>
          {row.isPaid ? (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#dcfce7', color: '#166534' }}>{t('sp_cp_paid', 'Paid')}</span>
          ) : row.isOverdue ? (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#fef3c7', color: '#92400e' }}>{t('sp_cp_overdue', 'Overdue')}</span>
          ) : (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: '#e2e8f0', color: '#475569' }}>{t('sp_cp_unpaid', 'Unpaid')}</span>
          )}
        </div>
      </div>
      <div style={{ padding: '0 14px 12px', display: 'flex', justifyContent: 'flex-end', gap: 10, alignItems: 'center' }}>
        {row.isPaid ? (
          <>
            <span style={{ fontSize: 12, color: '#475569' }}>
              {t('sp_cp_paid_on', 'Paid {date}', { date: row.paidAt ? fmtDate(row.paidAt) : '' })}{row.amountPaid != null ? ` · ${money(row.amountPaid, row.currency)}` : ''}{row.reference ? ` · ${t('sp_cp_reference', 'Reference #')} ${row.reference}` : ''}
            </span>
            <button onClick={() => onUndo(row)} disabled={rowBusy} style={ghostBtn}>{t('sp_cp_undo', 'Undo')}</button>
          </>
        ) : !open ? (
          <button onClick={() => setOpen(true)} style={primaryBtn}>{t('sp_cp_mark_paid', 'Mark paid')}</button>
        ) : (
          <div style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, background: '#fbfcfe' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, alignItems: 'end' }}>
              <div>
                <label style={lbl}>{t('sp_cp_amount_paid', 'Amount paid')} ({row.currency})</label>
                <input type="number" min="0" step="0.01" value={form.amountPaid} onChange={e => setForm(f => ({ ...f, amountPaid: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={lbl}>{t('sp_cp_reference', 'Reference #')}</label>
                <input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} style={inp} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>{t('sp_cp_note', 'Note')}</label>
                <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} style={inp} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
              <button onClick={() => setOpen(false)} disabled={rowBusy} style={ghostBtn}>{t('cancel', 'Cancel')}</button>
              <button onClick={() => onPay(row, form)} disabled={rowBusy} style={primaryBtn}>{rowBusy ? t('sp_pay_charging', 'Saving…') : t('sp_cp_save', 'Save')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const lbl = { fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }
const primaryBtn = { padding: '7px 14px', fontSize: 13, fontWeight: 600, border: 'none', background: '#0f172a', color: '#fff', borderRadius: 6, cursor: 'pointer' }
const ghostBtn = { padding: '7px 12px', fontSize: 12.5, fontWeight: 600, border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: 6, cursor: 'pointer' }
const pgBtn = (disabled) => ({ padding: '7px 14px', fontSize: 13, border: '1px solid #cbd5e1', background: disabled ? '#f1f5f9' : '#fff', color: disabled ? '#94a3b8' : '#0f172a', borderRadius: 6, cursor: disabled ? 'default' : 'pointer' })
